import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import DOMPurify from 'dompurify';
import { useReducedMotion } from '@mantine/hooks';
import { Joyride, ACTIONS, EVENTS, STATUS, type EventData, type Controls } from 'react-joyride';
import { useTypedSelector } from '@hooks/useTypedSelector';
import { useActions } from '@hooks/useActions';
import { useLocation, useNavigate } from 'react-router';
import TourTooltipBridge from './TourTooltipBridge';
import TourMotionLayer from './TourMotionLayer';
import { createTourPresentationStore, TourPresentationContext } from './TourPresentationContext';
import TourChainPrompt from './TourChainPrompt';
import TourEscapeHatch from './TourEscapeHatch';
import { TourControlsContext, type ITourControlsContext } from './TourControlsContext';
import { matchRoutePattern, waitForAction, consumePendingTour, savePendingTour } from './tourUtils';
import { parseStepData } from '@srm/core/contracts/tours';
import { useCurrentRoute } from './useCurrentRoute';
import { useTourTracking } from './useTourTracking';
import { useTourTargetTracking } from './useTourTargetTracking';
import type { ITourPublished } from '@models/tours/ITourPublished';

const TourProvider: FC = () => {
    const { tours, activeTourId, run, stepIndex, stepHistory } = useTypedSelector((state) => state.tours);
    const currentFlowName = useTypedSelector((state) => state.document?.scheme?.flowName);
    const {
        fetchTours,
        startTour,
        restoreTour,
        stopTour,
        setStepIndex,
        pushStepHistory,
        popStepHistory,
        completeTour,
        skipTour,
        updateProgress,
    } = useActions();
    const location = useLocation();
    const currentRoute = useCurrentRoute();
    const navigate = useNavigate();
    const tracking = useTourTracking();
    const reducedMotion = useReducedMotion();
    const autoStartedRef = useRef<Set<string>>(new Set());
    const explicitlyTrackedRef = useRef<Set<string>>(new Set());
    const pendingRestoreRef = useRef(false);
    const abortRef = useRef<AbortController | null>(null);
    const lastCompletedTourIdRef = useRef<string>('');
    const [suggestedTour, setSuggestedTour] = useState<ITourPublished | null>(null);
    const [actionDone, setActionDone] = useState(false);
    // Joyride deep-compares callbacks by source, so callbacks must read the current action state.
    const actionDoneRef = useRef(false);
    actionDoneRef.current = actionDone;
    const presentationStore = useMemo(createTourPresentationStore, []);
    const presentationContext = useMemo(
        () => ({ store: presentationStore, tourId: activeTourId ?? '' }),
        [presentationStore, activeTourId],
    );

    // Загрузить туры при монтировании
    useEffect(() => {
        fetchTours();
    }, []);

    // Восстановление pending-тура после мультистраничной навигации.
    // consumePendingTour() немедленно вычитывает и удаляет запись из sessionStorage,
    // pendingRestoreRef блокирует автозапуск до завершения restoreTour().
    useEffect(() => {
        if (tours.length === 0) return;

        const pending = consumePendingTour();
        if (!pending) return;

        const tour = tours.find((t) => t.id === pending.tourId);
        if (!tour) return;

        pendingRestoreRef.current = true;
        explicitlyTrackedRef.current.add(pending.tourId);

        // setTimeout даёт Redux batch от stopTour() применить activeTourId = null,
        // после чего restoreTour атомарно устанавливает все поля за один dispatch.
        setTimeout(() => {
            restoreTour(pending.tourId, pending.stepIndex, pending.stepHistory || []);
            tracking.trackTourRestored(pending.tourId, pending.stepIndex, pending.timestamp);
            pendingRestoreRef.current = false;
        }, 0);
    }, [location.pathname, tours]);

    // Автозапуск туров при навигации
    useEffect(() => {
        if (activeTourId || pendingRestoreRef.current) return;

        // route = pathname + hash: позволяет автозапуск на конкретной вкладке карточки
        // (например, «Выбор победителя» → #page-tlcCustomWinnerChoice).
        const route = currentRoute;
        const autoStartTour = tours.find(
            (t) =>
                t.isAutoStart &&
                (!t.progress || t.progress.status === 'not_started') &&
                !autoStartedRef.current.has(t.id) &&
                matchRoutePattern(t.routePattern, route) &&
                (!t.targeting?.flowNames?.length ||
                    (currentFlowName && t.targeting.flowNames.includes(currentFlowName))),
        );

        if (autoStartTour) {
            autoStartedRef.current.add(autoStartTour.id);
            explicitlyTrackedRef.current.add(autoStartTour.id);
            tracking.trackTourStarted(autoStartTour.id, true);
            const firstStepData = parseStepData(autoStartTour.steps[0]?.data);
            if (firstStepData?.navigate?.url && location.pathname !== firstStepData.navigate.url) {
                tracking.trackPageNavigated(autoStartTour.id, 0, firstStepData.navigate.url);
                savePendingTour(autoStartTour.id, 0);
                navigate(firstStepData.navigate.url);
            } else {
                startTour(autoStartTour.id);
            }
        }
    }, [currentRoute, tours, activeTourId, currentFlowName]);

    const activeTour = tours.find((t) => t.id === activeTourId);
    const activeStep = run ? activeTour?.steps[stepIndex] : undefined;
    useTourTargetTracking(activeStep?.target, activeStep?.spotTarget);

    const { trackPageNavigated, trackStepViewed } = tracking;
    const transitionToStep = useCallback(
        (sourceIndex: number, targetIndex: number) => {
            if (!activeTour) return;
            pushStepHistory(sourceIndex);
            const targetStep = activeTour.steps[targetIndex];
            const targetData = parseStepData(targetStep?.data);
            if (targetData?.navigate?.url) {
                trackPageNavigated(activeTour.id, targetIndex, targetData.navigate.url);
                savePendingTour(activeTour.id, targetIndex, [...stepHistory, sourceIndex]);
                stopTour();
                navigate(targetData.navigate.url);
                return;
            }
            setStepIndex(targetIndex);
            updateProgress(activeTour.id, 'in_progress', targetIndex);
            trackStepViewed(activeTour.id, targetIndex);
        },
        [
            activeTour,
            stepHistory,
            pushStepHistory,
            stopTour,
            navigate,
            setStepIndex,
            updateProgress,
            trackPageNavigated,
            trackStepViewed,
        ],
    );

    // Трекинг ручного запуска тура (из TourLauncher).
    // Автозапуск, chain-accept и restore трекаются явно и помечаются в explicitlyTrackedRef.
    const prevActiveTourRef = useRef<string | null>(null);
    useEffect(() => {
        if (activeTourId && activeTourId !== prevActiveTourRef.current) {
            if (explicitlyTrackedRef.current.has(activeTourId)) {
                // Уже протрекирован явно — очищаем метку, не дублируем
                explicitlyTrackedRef.current.delete(activeTourId);
            } else {
                // Ручной запуск из TourLauncher — единственный путь сюда
                tracking.trackTourStarted(activeTourId, false);
            }
        }
        prevActiveTourRef.current = activeTourId;
    }, [activeTourId, tracking]);

    // Action-based шаги: слушаем DOM-событие на текущем шаге
    useEffect(() => {
        if (!activeTour || !run) return;

        const currentStep = activeTour.steps[stepIndex];
        if (!currentStep) return;

        const stepData = parseStepData(currentStep.data);
        if (!stepData?.action) return;

        setActionDone(false);
        abortRef.current?.abort();
        const ac = new AbortController();
        abortRef.current = ac;

        const advanceToNext = () => {
            tracking.trackStepCompleted(activeTour.id, stepIndex);

            const next = stepIndex + 1;
            if (next >= activeTour.steps.length) {
                handleTourFinished(activeTour);
                return;
            }
            transitionToStep(stepIndex, next);
        };

        waitForAction(stepData.action, currentStep.target, ac.signal)
            .then(() => {
                if (ac.signal.aborted) return;

                tracking.trackActionCompleted(activeTour.id, stepIndex, stepData.action!.type);

                if (stepData.action!.autoAdvance === false) {
                    // autoAdvance: false — показываем «Далее», пользователь решает сам
                    setActionDone(true);
                } else {
                    advanceToNext();
                }
            })
            .catch((err) => {
                // AbortError при смене шага/размонтировании — штатная ситуация
                if (err?.name === 'AbortError') return;
                // Таймаут ожидания элемента — пропускаем шаг вперёд как fallback
                tracking.trackActionTimeout(activeTour.id, stepIndex, stepData.action!.type);
                advanceToNext();
            });

        return () => {
            ac.abort();
        };
    }, [activeTour, run, stepIndex]);

    // Авто-скролл к target'у текущего шага, если у него enableScroll.
    // Скроллим только ближайший scrollable-родитель (например, .doc-tab),
    // а не всю цепочку, чтобы не тянуть страницу в целом.
    useEffect(() => {
        if (!activeTour || !run) return;
        const step = activeTour.steps[stepIndex];
        if (!step) return;
        const stepData = parseStepData(step.data);
        if (!stepData?.enableScroll) return;

        const findScrollableParent = (el: Element): Element | null => {
            let p: Element | null = el.parentElement;
            while (p && p !== document.body) {
                const s = getComputedStyle(p);
                if (['auto', 'scroll', 'overlay'].includes(s.overflowY) && p.scrollHeight > p.clientHeight + 1) {
                    return p;
                }
                p = p.parentElement;
            }
            return null;
        };

        // Поллим target несколько раз — может быть отложенный рендер
        let attempts = 0;
        const iv = setInterval(() => {
            attempts += 1;
            const el = document.querySelector(step.scrollTarget || step.target);
            if (el) {
                clearInterval(iv);
                const scroller = findScrollableParent(el);
                if (scroller) {
                    const elRect = el.getBoundingClientRect();
                    const scRect = scroller.getBoundingClientRect();
                    const offset = elRect.top - scRect.top - scRect.height / 2 + elRect.height / 2;
                    scroller.scrollTo({
                        top: scroller.scrollTop + offset,
                        behavior: reducedMotion ? 'auto' : 'smooth',
                    });
                } else {
                    el.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'center' });
                }
            } else if (attempts >= 20) {
                clearInterval(iv);
            }
        }, 150);
        return () => clearInterval(iv);
    }, [activeTour, run, stepIndex, reducedMotion]);

    const steps = useMemo(() => {
        if (!activeTour) return [];
        return activeTour.steps.map((s) => {
            const stepData = parseStepData(s.data);
            const spotlightTarget = s.spotTarget;
            return {
                target: s.target,
                title: s.title,
                content: (
                    <div
                        dangerouslySetInnerHTML={{
                            __html: DOMPurify.sanitize(s.content ?? '', { ADD_ATTR: ['target'] }),
                        }}
                    />
                ),
                placement: s.placement as any,
                skipBeacon: true,
                isFixed: s.isFixed,
                // Отключаем автоскролл Joyride: он конкурирует со scroll-listener
                // AnchoredDialog (popup пересчитывает позицию на scroll), и к тому же тянет
                // всё окно. Если нужен скролл к target'у — это делает отдельный useEffect
                // по флагу data.enableScroll, который скроллит только ближайший scrollable-родитель.
                skipScroll: true,
                data: stepData,
                ...(spotlightTarget ? { spotlightTarget } : {}),
                ...(s.scrollTarget ? { scrollTarget: s.scrollTarget } : {}),
                hideOverlay: true,
                disableFocusTrap: true,
            };
        });
    }, [activeTour]);

    const handleEvent = useCallback(
        (data: EventData, _controls: Controls) => {
            if (!activeTour) return;

            const { action, index, status, type } = data;

            if ([EVENTS.STEP_AFTER, EVENTS.TARGET_NOT_FOUND].includes(type as any)) {
                if (action === ACTIONS.PREV) {
                    // «Назад» — используем стек истории
                    const prevIndex = stepHistory[stepHistory.length - 1];
                    if (prevIndex !== undefined) {
                        popStepHistory();
                        setStepIndex(prevIndex);
                        updateProgress(activeTour.id, 'in_progress', prevIndex);
                        tracking.trackStepViewed(activeTour.id, prevIndex);
                    }
                } else {
                    // Action-based шаги: «Далее» обрабатывается через useEffect,
                    // кроме случая autoAdvance: false, где actionDone разрешает переход
                    const currentStep = activeTour.steps[index];
                    if (currentStep) {
                        const currentStepData = parseStepData(currentStep.data);
                        if (type !== EVENTS.TARGET_NOT_FOUND && currentStepData?.action && !actionDoneRef.current)
                            return;
                    }

                    tracking.trackStepCompleted(activeTour.id, index);

                    transitionToStep(index, index + 1);
                }
            }

            if (status === STATUS.FINISHED) {
                handleTourFinished(activeTour);
            }

            if (status === STATUS.SKIPPED) {
                tracking.trackTourSkipped(activeTour.id, stepIndex);
                skipTour(activeTour.id);
            }
        },
        [activeTour, stepHistory, transitionToStep],
    );

    const handleTourFinished = useCallback(
        (tour: ITourPublished) => {
            tracking.trackTourCompleted(tour.id, tour.steps.length);
            lastCompletedTourIdRef.current = tour.id;

            const lastStep = tour.steps[tour.steps.length - 1];
            const lastData = parseStepData(lastStep?.data);

            if (lastData?.nextTourCode) {
                const nextTour = tours.find((t) => t.code === lastData.nextTourCode);
                if (nextTour && nextTour.progress?.status !== 'completed') {
                    completeTour(tour.id);
                    tracking.trackChainOffered(tour.id, lastData.nextTourCode);
                    setTimeout(() => setSuggestedTour(nextTour), 500);
                    return;
                }
            }

            completeTour(tour.id);
        },
        [tours, tracking],
    );

    // Ветвление: переход на произвольный шаг
    const goToStep = useCallback(
        (targetIndex: number, branchLabel?: string) => {
            if (!activeTour) return;

            if (branchLabel) {
                tracking.trackBranchSelected(activeTour.id, stepIndex, branchLabel, targetIndex);
            }
            tracking.trackStepCompleted(activeTour.id, stepIndex);

            transitionToStep(stepIndex, targetIndex);
        },
        [activeTour, stepIndex, tracking, transitionToStep],
    );

    const canGoBack = stepHistory.length > 0;
    const controlsContextValue = useMemo<ITourControlsContext>(
        () => ({ goToStep, canGoBack, actionDone }),
        [goToStep, canGoBack, actionDone],
    );

    const handleAcceptChain = useCallback(
        (tour: ITourPublished) => {
            const sourceTourId = lastCompletedTourIdRef.current;
            if (sourceTourId) {
                tracking.trackChainAccepted(sourceTourId, tour.code);
            }
            setSuggestedTour(null);
            explicitlyTrackedRef.current.add(tour.id);
            tracking.trackTourStarted(tour.id, false);
            const firstStepData = parseStepData(tour.steps[0]?.data);
            if (firstStepData?.navigate?.url && location.pathname !== firstStepData.navigate.url) {
                tracking.trackPageNavigated(tour.id, 0, firstStepData.navigate.url);
                savePendingTour(tour.id, 0);
                navigate(firstStepData.navigate.url);
            } else {
                startTour(tour.id);
            }
        },
        [location.pathname, tracking],
    );

    const handleDismissChain = useCallback(() => {
        const sourceTourId = lastCompletedTourIdRef.current;
        if (suggestedTour && sourceTourId) {
            tracking.trackChainRejected(sourceTourId, suggestedTour.code);
        }
        setSuggestedTour(null);
    }, [suggestedTour, tracking]);

    const locale = useMemo(() => {
        const parsed = activeTour?.locale ? JSON.parse(activeTour.locale) : undefined;
        return {
            back: 'Назад',
            next: 'Далее',
            skip: 'Завершить тур',
            last: 'Завершить',
            close: 'Закрыть',
            ...parsed,
        };
    }, [activeTour?.locale]);

    // Пока тур активен — блокируем скролл внешних контейнеров (main-content и body),
    // чтобы пользователь случайно не увёл заголовок карточки под navbar.
    // Скролл внутреннего .doc-tab остаётся доступным (см. правило в TourEscapeHatch.scss).
    useEffect(() => {
        if (!activeTour || !run) return;
        document.body.classList.add('tour-running');
        return () => document.body.classList.remove('tour-running');
    }, [activeTour, run]);

    const handleEscapeHatch = useCallback(() => {
        if (!activeTour) return;
        tracking.trackTourSkipped(activeTour.id, stepIndex);
        skipTour(activeTour.id);
    }, [activeTour, skipTour, stepIndex, tracking]);

    return (
        <>
            {activeTour && run && <TourEscapeHatch onExit={handleEscapeHatch} />}
            {activeTour && run && (
                <TourPresentationContext.Provider value={presentationContext}>
                    <Joyride
                        steps={steps}
                        run={run}
                        stepIndex={stepIndex}
                        continuous
                        onEvent={handleEvent}
                        tooltipComponent={TourTooltipBridge}
                        floatingOptions={{ hideArrow: true }}
                        loaderComponent={null}
                        locale={locale}
                        options={{
                            zIndex: 10000,
                            targetWaitTimeout: 5000,
                            // Отключаем закрытие тура по клику на overlay:
                            // в controlled mode close() из joyride не синхронизирует Redux,
                            // что оставляет overlay без tooltip.
                            overlayClickAction: false,
                            dismissKeyAction: false,
                        }}
                    />
                </TourPresentationContext.Provider>
            )}
            <TourControlsContext.Provider value={controlsContextValue}>
                <TourMotionLayer
                    store={presentationStore}
                    tourId={activeTourId}
                    running={!!activeTour && run}
                    stepIndex={stepIndex}
                />
            </TourControlsContext.Provider>
            {suggestedTour && (
                <TourChainPrompt tour={suggestedTour} onAccept={handleAcceptChain} onDismiss={handleDismissChain} />
            )}
        </>
    );
};

export default TourProvider;
